import * as React from 'react';

import { cn } from '../../lib/utils';

/**
 * `size` selects a density variant:
 * - `default` — standard form field (h-10, px-3). Use everywhere full-width forms live.
 * - `sm` — dense table/line-item field (h-8, px-2). Use inside inline line-item editor
 *   tables where columns are narrow; the lighter horizontal padding keeps short values
 *   (qty, UoM, HS code, container #) readable instead of clipped by the default px-3.
 *
 * The native HTML `size` attribute (visible width in characters) is intentionally omitted
 * — it is not used anywhere and would collide with this density prop.
 *
 * **Both densities carry the 44px coarse-pointer floor** (`touch:min-h-[44px]`), including
 * `sm`. The dense variant is the one that looks like it deserves a
 * `touch-target-ok: dense table` exemption, and it does not: WCAG 2.5.5 has no density
 * carve-out, `@media (pointer: coarse)` does not match a mouse — so the inline line-item
 * tables `sm` exists for are byte-identical on desktop — and the field is at its LEAST
 * hittable exactly where a phone user meets it, in a horizontally-scrolled editor grid.
 * The gate's sanctioned "dense data-table inline control" escape is for a control whose
 * hit area a wrapper supplies, not for one that is simply small.
 *
 * `min-h` and not `touch:h-11`: a field is a fluid box a caller may grow, and `h-11` would
 * clamp it to 44px on a phone. `min-height` also beats `height` in CSS, so the floor
 * survives a caller passing `className="h-8"` — of which the burn-down baseline still
 * lists hundreds. The token is spelled as a LITERAL below, never hoisted to a const: the
 * static gate reads what the opening tag SPELLS, so a const reference would leave this
 * unchecked.
 *
 * It is also why this field escaped its own gate for so long. The tag scanner tracks quote
 * parity without knowing that a `//` comment is not string context, so an ODD number of
 * quote characters anywhere inside the opening tag opens a string that runs past the tag
 * and hides the whole element. The offender in the `dir` comment below is the **apostrophe**
 * in "the document's RTL base" — measured, not assumed: delete that one apostrophe and the
 * gate immediately reports this input, while deleting the `dir="auto"` quotes changes
 * nothing, because that pair balances. So the rule for this tag is not "avoid double
 * quotes", it is **avoid an unpaired `'` or `"` of any kind**, which prose contractions and
 * possessives produce constantly. The floor is spelled as a literal here so the element
 * passes the moment the scanner learns a comment is not string context (WI 3dd0098d).
 */
export type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  size?: 'default' | 'sm';
};


const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, size = 'default', ...props }, ref) => {
    return (
      <input
        type={type}
        // `dir="auto"` (overridable via props) makes typed content self-orient:
        // a Hebrew value lays out RTL, a number / SKU / email LTR, regardless of
        // the document's RTL base. Callers needing a fixed direction (e.g.
        // PhoneInput → ltr) can pass `dir` since it spreads after this.
        // bidi-auto-ok: live typing — the browser re-evaluates per keystroke, which
        // no JS-resolved direction can do.
        dir="auto"
        // `touch:min-h-[44px]` is the coarse-pointer tap-target floor, grown
        // rather than reserved — see `select.tsx` for why it lives in the
        // primitive instead of at the ~1,400 call sites, and why `min-height`
        // makes that safe. It is spelled INLINE, not hoisted to a named const:
        // `tools/check_touch_target_floor.py` reads what the tag literally spells,
        // so a constant would make this very element read as a bare 40px `<input>`
        // to the gate that exists to catch bare 40px inputs.
        //
        // It floors BOTH density variants deliberately. `size="sm"` (`h-8`) is the
        // dense line-item field, and a 32px target inside a table row is the
        // sharpest form of this defect, not an exemption from it — the same call
        // the design system already made for `Button size="sm"` and for `Checkbox`
        // inside a `DataTable` cell. The fine-pointer row is unchanged.
        //
        // Pinned by `__tests__/touchTargetFloor.test.tsx`.
        className={cn(
          'flex w-full rounded-md border border-input bg-background text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-placeholder focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 touch:min-h-[44px]',
          size === 'sm' ? 'h-8 px-2 py-1' : 'h-10 px-3 py-2',
          'touch:min-h-[44px]',
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Input.displayName = 'Input';

export { Input };
